import { ChartBarIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { isTrialMode } from '../../auth/trial-session';
import { InventoryValueDisplay } from '../../components/dashboard/inventory-value-display';
import { StoreSalesStatsDisplay } from '../../components/dashboard/store-sales-stats-display';
import { EmptyState } from '../../components/empty-state';
import { PrimaryLinkButton } from '../../components/link-buttons';
import { useStore } from '../../hooks/use-store';

export function Dashboard() {
  const store = useStore();

  if (store.last_uploaded_at == null) {
    return (
      <EmptyState
        icon={<RocketLaunchIcon className="size-8" />}
        title="Your store hasn't uploaded any data yet"
        description="Finish setting up your store to start syncing sales and inventory."
        action={
          // Onboarding is Stripe checkout and the Companion; a trial's data
          // comes from the capture exe (ORD-514).
          isTrialMode() ? undefined : (
            <PrimaryLinkButton
              to="/onboard/$storeId"
              params={{ storeId: store.id }}
              search={{ checkout: undefined }}
            >
              Complete your Onboarding
            </PrimaryLinkButton>
          )
        }
      />
    );
  }

  if (store.stats.sales.historical.length === 0) {
    return (
      <EmptyState
        icon={<ChartBarIcon className="size-8" />}
        title="Your dashboard will populate once you start selling"
        description="Sales, margin, and transaction trends will appear here once you start ringing up sales and your next upload runs."
      />
    );
  }

  return (
    // One card per row below a laptop, two from lg, three from wide (1440).
    // The cards have no width of their own — they take a column and everything
    // inside sizes off that, so the only widths here are these three.
    //
    // Two columns waits for lg rather than md: a summary row is four tiles, and
    // each tile is at least as wide as its "+4.0% YoY" subline, so the row
    // needs ~334px of card whatever the heading does. Two-up at md leaves 332.
    // The scroller and the grid are deliberately two elements. A card sets
    // overflow-hidden, which gives a grid item an automatic minimum size of
    // zero, so a grid with a definite height compresses every card to a
    // fraction of its content instead of overflowing. Scrolling on the outside
    // leaves the grid's height indefinite, and the rows size to their cards.
    <div className="h-full w-full overflow-y-auto bg-gray-200">
      <div className="grid grid-cols-1 p-4 gap-2 lg:grid-cols-2 wide:grid-cols-3">
        <StoreSalesStatsDisplay store={store} displayType="Sales" />
        <StoreSalesStatsDisplay store={store} displayType="Gross Margin" />
        <StoreSalesStatsDisplay store={store} displayType="Transactions" />
        <StoreSalesStatsDisplay store={store} displayType="Average Order Value" />
        <InventoryValueDisplay store={store} />
      </div>
    </div>
  );
}
